import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteButton,
  Edit,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { CustomActions } from '../../components';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { isValidDoubleJSON } from '../../utils';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomToolbar = ({checklist}) => {
  const record = useRecordContext();
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const backgroundColor = theme.palette.background.paper; // Default background color
  const isDeletable = record.route_checkpoints.items.length === 0;

  const handleCancel = () => {
    redirect('list', 'checkpoints');
  };

  let isDifferent = false;

  if (isValidDoubleJSON(record.checklist)) {
    isDifferent = !_.isEqual(JSON.parse(JSON.parse(record.checklist)).items.sort((a, b) => a.order - b.order), checklist);
  }

  const handleSuccessUpdate = async (res) => {
    try {
      const id = res.id;
      const data = {
        id: res.id,
        name: res.name,
        description: res.description,
        checklist: JSON.stringify(JSON.stringify({items: checklist})),
        site_id: res.site_id,
        camera_id: res.camera_id,
      };

      await dataProvider.update("checkpoints", {id, data });
      notify('Checkpoint updated successfully', { type: 'success' });
      refresh();
      redirect('list', 'checkpoints');
    } catch (error) {
      console.error('Error updating checkpoint:', error);
      notify('Error updating checkpoint', { type: 'error' });
    }
  };

  const handleSuccessDelete = async (res) => {
    try {
      const id = res.id;
      const previousData = res;
      
      await dataProvider.delete("checkpoints", {id, previousData});
      notify('Checkpoint deleted successfully', { type: 'success' });
      redirect('list', 'checkpoints');
    } catch (error) {
      console.error('Error deleting checkpoint:', error);
      notify('Error deleting checkpoint', { type: 'error' });
    }
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <Box display='flex' alignItems='center'>
          <DeleteButton
            type='button'
            size='small'
            label='Delete'
            sx={{textTransform: 'none', minWidth: '95px', minHeight: '30.75px'}}
            mutationOptions={{ onSuccess: handleSuccessDelete }}
            disabled={!isDeletable}
          />
          {!isDeletable && (
            <Tooltip title='This checkpoint is referenced by other routes.' placement='top'>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </Tooltip>
          )}
        </Box>
        <Box>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: theme.palette.common.white, // Optional: primary color for cancel button text
              borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
              marginRight: theme.spacing(1),
              textTransform: 'none',
              minWidth: '95px',
            }}
            size='small'
          >
            Cancel
          </Button>
          <SaveButton
            alwaysEnable={isDifferent}
            type='button'
            label='Update'
            size='small'
            icon={null}
            sx={{textTransform: 'none', minWidth: '95px'}}
            mutationOptions={{ onSuccess: handleSuccessUpdate }}
          />
        </Box>
      </Box>
    </Toolbar>
  );
};

const CheckList = ({onChange}) => {
  const record = useRecordContext();
  const [checks, setChecks] = useState([]);
  const [checkValue, setCheckValue] = useState("");

  useEffect(() => {
    if (isValidDoubleJSON(record.checklist)) {
      const parsedChecks = JSON.parse(JSON.parse(record.checklist));
      setChecks(parsedChecks.items.sort((a, b) => a.order - b.order));
    }
  }, [record]);

  useEffect(() => {
    onChange(checks);
  }, [checks]);

  const handleChangeCheckInput = (event) => {
    setCheckValue(event.target.value);
  };

  const handleAddCheck = () => {
    if (checkValue.trim()) {
      const updateChecks = [...checks, { item: checkValue.trim(), order: checks.length }];
      setChecks(updateChecks);
      setCheckValue("");
    }
  }

  const handleDelete = (order) => {
    const updatedChecks = checks.filter((check) => check.order !== order);

    // Reassign order values to maintain sequence (0,1,2,...)
    const reorderedChecks = updatedChecks.map((check, index) => ({
      ...check,
      order: index,
    }));

    setChecks(reorderedChecks);
  }

  const moveUp = (orderNum) => {
    if (orderNum === 0) return; // Prevent moving the first item up
    swapOrder(orderNum, orderNum - 1);
  }

  const moveDown = (orderNum) => {
    if (orderNum === checks.length - 1) return; // Prevent moving the last item down
    swapOrder(orderNum, orderNum + 1);
  }

  // Swap the order values between two items
  const swapOrder = (order1, order2) => {
    const updatedChecks = [...checks];
    const index1 = updatedChecks.findIndex((item) => item.order === order1);
    const index2 = updatedChecks.findIndex((item) => item.order === order2);

    if (index1 !== -1 && index2 !== -1) {
      // Swap the order values
      [updatedChecks[index1].order, updatedChecks[index2].order] = [
        updatedChecks[index2].order,
        updatedChecks[index1].order,
      ];

      // Update the state with the new sorted order
      setChecks(updatedChecks.sort((a, b) => a.order - b.order));
    }
  };

  return (
    <div style={{ width: '100%'}} >
      {checks?.length > 0 && checks.map((check, index, array) => (
        <div
          key={index}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: 'rgb(30, 30, 46)',
            border: '1px solid rgb(120, 120, 120)',
            marginBottom: '5px',
          }}
        >
          <div>{index + 1}</div>
          <div
            style={{
              margin: 'auto',
              marginLeft: '10px'
            }}
          >{check.item}</div>
          <IconButton disabled={index === 0} onClick={() => moveUp(check.order)}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <IconButton disabled={index === array.length - 1} onClick={() => moveDown(check.order)}>
            <KeyboardArrowDownIcon />
          </IconButton>
          <IconButton onClick={() => handleDelete(check.order)}>
            <DeleteIcon />
          </IconButton>
        </div>
      ))}
      <TextField
        label="Add Check"
        value={checkValue}
        onChange={handleChangeCheckInput}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleAddCheck} edge="end">
                <AddCircleOutlineIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};

const EditCheckpoint = () => {
  const [changedChecks, setChangedChecks] = useState([]);

  return (
    <Card variant='elevation' sx={{ width: '50%', overflowY: 'auto', scrollbarWidth: 'none' }}>
      <CardContent>
        <Edit actions={<CustomActions title="Edit Checkpoint" type="edit" />}>
          <SimpleForm toolbar={<CustomToolbar checklist={changedChecks} />} >
            <Typography variant="subtitle2">
              Details
            </Typography>
            <TextInput label="Name" source="name" fullWidth validate={[required('Name is required')]} />
            <TextInput source="description" multiline minRows={5} fullWidth />
            <ReferenceInput source="site_id" reference="sites" label="Select a site">
              <SelectInput optionText="name" fullWidth validate={[required('Site selection is required')]} />
            </ReferenceInput>
            <ReferenceInput source="camera_id" reference="cameras" label="Select Camera">
              <SelectInput optionText="name" fullWidth validate={[required('Site selection is required')]} />
            </ReferenceInput>
            <Typography variant="subtitle2" gutterBottom>
              Checklist
            </Typography>
            <CheckList onChange={setChangedChecks} />
          </SimpleForm>
        </Edit>
      </CardContent>
    </Card>
  )
};

export default EditCheckpoint;
export { EditCheckpoint };